// ============================================================
// Stocora — LoginPage (rewritten 2026-07-19: password auth)
// ============================================================
// Replaces the magic-link flow (1B.1, 2026-04-24) with standard
// email + password. Invite-only posture is UNCHANGED — there is
// still no public sign-up; accounts are created exclusively via
// the new Settings › Team invite flow (invite-clinic-member Edge
// Function), which is how a would-be user gets credentials in the
// first place. See DECISIONS.md 2026-07-19 "Password auth +
// admin-managed invites" for the full rationale.
//
// Forgot-password uses Supabase's standard resetPasswordForEmail,
// which redirects through /auth/callback → SetPasswordPage — the
// same landing page an invite link uses. One "set your password"
// surface serves both first-time setup and resets.
//
// `?next=` / `?error=` handling and the already-authenticated
// bounce are carried over unchanged from the magic-link version.
// ============================================================

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { DEFAULT_POST_LOGIN_PATH, safeNextPath } from '@/lib/auth';
import Icon from '@/components/Icon';
import StocoraLogo from '@/components/StocoraLogo';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESET_COOLDOWN_SECONDS = 60;

interface LoginFormValues {
  email: string;
  password: string;
}

type ViewState =
  | { kind: 'form' }
  | { kind: 'signing_in' }
  | { kind: 'reset_sent'; email: string; cooldownRemaining: number };

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<ViewState>({ kind: 'form' });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginFormValues>({
    mode: 'onBlur',
    defaultValues: { email: '', password: '' },
  });

  // Bounce already-authenticated users. Honors ?next=.
  useEffect(() => {
    let cancelled = false;
    void supabase.auth.getSession().then(({ data }) => {
      if (!cancelled && data.session) {
        navigate(safeNextPath(searchParams.get('next')), { replace: true });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [navigate, searchParams]);

  // Surface errors forwarded from /auth/callback or the auth guard.
  useEffect(() => {
    const error = searchParams.get('error');
    if (!error) return;
    const description = searchParams.get('error_description');
    setErrorMessage(describeLoginUrlError(error, description));
  }, [searchParams]);

  // Cooldown countdown while a reset email is pending re-send.
  useEffect(() => {
    if (state.kind !== 'reset_sent' || state.cooldownRemaining <= 0) return;
    const timer = window.setTimeout(() => {
      setState((s) =>
        s.kind === 'reset_sent'
          ? { ...s, cooldownRemaining: s.cooldownRemaining - 1 }
          : s,
      );
    }, 1000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [state]);

  const signIn = async (email: string, password: string): Promise<void> => {
    setErrorMessage(null);
    setState({ kind: 'signing_in' });

    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        setErrorMessage(describeSignInError(error.message, error.status));
        setState({ kind: 'form' });
        return;
      }

      navigate(safeNextPath(searchParams.get('next')), { replace: true });
    } catch (err) {
      setErrorMessage('Network error. Check your connection and try again.');
      setState({ kind: 'form' });
      console.error('signInWithPassword threw:', err);
    }
  };

  const sendResetEmail = async (email: string): Promise<void> => {
    setErrorMessage(null);

    const redirectUrl = new URL('/auth/callback', window.location.origin);
    const safeNext = safeNextPath(searchParams.get('next'));
    if (safeNext !== DEFAULT_POST_LOGIN_PATH) {
      redirectUrl.searchParams.set('next', safeNext);
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl.toString(),
      });

      if (error) {
        setErrorMessage(describeSignInError(error.message, error.status));
        setState({ kind: 'form' });
        return;
      }

      // Deliberately the same "check your email" state regardless of
      // whether the address exists — resetPasswordForEmail should not
      // be used to enumerate valid accounts.
      setState({ kind: 'reset_sent', email, cooldownRemaining: RESET_COOLDOWN_SECONDS });
    } catch (err) {
      setErrorMessage('Network error. Check your connection and try again.');
      setState({ kind: 'form' });
      console.error('resetPasswordForEmail threw:', err);
    }
  };

  const onSubmit = handleSubmit((data) => {
    void signIn(data.email.trim(), data.password);
  });

  const onForgotPassword = (): void => {
    const email = getValues('email').trim();
    if (!email || !EMAIL_PATTERN.test(email)) {
      setErrorMessage('Enter your email above first, then tap "Forgot password?" again.');
      return;
    }
    void sendResetEmail(email);
  };

  const onResendReset = (): void => {
    if (state.kind !== 'reset_sent' || state.cooldownRemaining > 0) return;
    void sendResetEmail(state.email);
  };

  const onBackToSignIn = (): void => {
    setState({ kind: 'form' });
    setErrorMessage(null);
  };

  // ---- Render -----------------------------------------------------------

  if (state.kind === 'reset_sent') {
    return (
      <div className="p-6 flex flex-col items-center text-center">
        <div className="w-14 h-14 rounded-2xl bg-success-surface flex items-center justify-center mb-4">
          <Icon name="mail" className="w-7 h-7 text-success" strokeWidth={2} />
        </div>
        <h1 className="text-lg font-semibold text-slate-900">Check your email</h1>
        <p className="text-sm text-slate-600 mt-1.5 max-w-[280px]">
          If an account exists for{' '}
          <span className="font-medium text-slate-900">{state.email}</span>, we sent a link to
          set a new password.
        </p>

        <div className="mt-6 w-full flex flex-col gap-2">
          <button
            type="button"
            onClick={onResendReset}
            disabled={state.cooldownRemaining > 0}
            className="w-full h-11 rounded-xl bg-slate-100 hover:bg-slate-200 active:bg-slate-300 disabled:bg-slate-50 disabled:text-slate-400 transition text-sm font-medium text-slate-800"
          >
            {state.cooldownRemaining > 0
              ? `Resend available in ${state.cooldownRemaining}s`
              : 'Resend link'}
          </button>
          <button
            type="button"
            onClick={onBackToSignIn}
            className="w-full h-11 rounded-xl hover:bg-slate-50 active:bg-slate-100 transition text-sm font-medium text-slate-600"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  const isSigningIn = state.kind === 'signing_in';

  return (
    <div className="p-6 flex flex-col min-h-full">
      <div className="flex flex-col items-center text-center pt-4">
        <div className="mb-5">
          <StocoraLogo size={32} />
        </div>
        <h1 className="text-lg font-semibold text-slate-900">Sign in to your practice</h1>
        <p className="text-sm text-slate-600 mt-1.5">Enter your clinic email and password.</p>
      </div>

      <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-3" noValidate>
        <div>
          <label
            htmlFor="login-email"
            className="block text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5"
          >
            Email
          </label>
          <input
            id="login-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            disabled={isSigningIn}
            placeholder="you@clinic.com"
            className={`w-full h-11 px-3.5 rounded-xl border text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-action/20 transition ${
              errors.email
                ? 'border-red-300 focus:border-red-500'
                : 'border-slate-200 focus:border-action'
            } ${isSigningIn ? 'bg-slate-50' : 'bg-white'}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: EMAIL_PATTERN, message: 'Please enter a valid email address' },
            })}
          />
          {errors.email && (
            <p className="text-xs text-red-600 mt-1.5">{errors.email.message ?? 'Invalid email'}</p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label
              htmlFor="login-password"
              className="block text-[11px] uppercase tracking-wide text-slate-500 font-semibold"
            >
              Password
            </label>
            <button
              type="button"
              onClick={onForgotPassword}
              className="text-[11px] font-medium text-action hover:text-action-hover"
            >
              Forgot password?
            </button>
          </div>
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            disabled={isSigningIn}
            placeholder="••••••••"
            className={`w-full h-11 px-3.5 rounded-xl border text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-action/20 transition ${
              errors.password
                ? 'border-red-300 focus:border-red-500'
                : 'border-slate-200 focus:border-action'
            } ${isSigningIn ? 'bg-slate-50' : 'bg-white'}`}
            {...register('password', { required: 'Password is required' })}
          />
          {errors.password && (
            <p className="text-xs text-red-600 mt-1.5">{errors.password.message}</p>
          )}
        </div>

        {errorMessage && (
          <div className="rounded-xl bg-red-50 border border-red-100 px-3 py-2.5">
            <p className="text-xs text-red-700 leading-relaxed">{errorMessage}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isSigningIn}
          className="w-full h-11 mt-1 rounded-xl bg-action hover:bg-action-hover active:bg-action-active disabled:opacity-60 transition text-sm font-semibold text-white flex items-center justify-center gap-2"
        >
          {isSigningIn ? (
            <>
              <Spinner />
              Signing in…
            </>
          ) : (
            'Sign in'
          )}
        </button>
      </form>

      <p className="text-[11px] text-slate-400 text-center mt-auto pt-6">
        Not registered? Ask your practice manager for access.
      </p>
    </div>
  );
}

// ---- helpers ------------------------------------------------------------

function describeSignInError(message: string, status: number | undefined): string {
  const msg = message.toLowerCase();

  if (msg.includes('invalid login credentials') || msg.includes('invalid email or password')) {
    return 'Incorrect email or password.';
  }

  if (msg.includes('email not confirmed')) {
    return 'This account hasn\u2019t finished setup yet. Check your inbox for the invite link.';
  }

  if (status === 429 || msg.includes('rate limit') || msg.includes('too many')) {
    return 'Too many attempts. Wait a minute and try again.';
  }

  if (msg.includes('network') || msg.includes('failed to fetch')) {
    return 'Network error. Check your connection and try again.';
  }

  return "Couldn't sign you in. Try again \u2014 or contact your clinic admin if this keeps happening.";
}

/**
 * Describes errors delivered via `?error=` on the login URL. Sources:
 *   - /auth/callback bounce (expired / invalid / consumed link,
 *     code-exchange failure, exchange timeout)
 *   - auth guard bounce (session_expired)
 */
function describeLoginUrlError(error: string, description: string | null): string {
  if (error === 'access_denied') {
    return "That link has expired or was already used \u2014 sometimes an email provider's security scanner opens the link automatically before you click it. Request a new one below.";
  }
  if (error === 'session_expired') {
    return 'Your session ended. Sign in again to continue.';
  }
  if (description) {
    return description;
  }
  return 'Something went wrong signing you in.';
}

function Spinner() {
  return (
    <span
      className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"
      aria-hidden="true"
    />
  );
}
